import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  ArrowLeft, 
  MapPin, 
  ExternalLink, 
  Copy, 
  Check,
  Settings,
  RefreshCw,
  Clock,
  AlertTriangle,
  Radio
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export default function DetailsPage({ objectId, objectData, onEdit, onBack }) {
  const [copied, setCopied] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentData, setCurrentData] = useState(objectData);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const refreshData = async () => {
    setIsRefreshing(true);
    try {
      const response = await api.get(`/objects/${encodeURIComponent(objectId)}/check`);
      if (response.data.exists) {
        setCurrentData(response.data.data);
        toast.success("Daten aktualisiert");
      }
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    } finally {
      setIsRefreshing(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Kopiert!");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Kopieren fehlgeschlagen");
    }
  };

  const openInMaps = () => {
    const location = currentData?.description?.location || {};
    const coords = location.coords || {};
    const lat = coords.latitude;
    const lng = coords.longitude;
    
    if (lat && lng) {
      const url = `https://www.google.com/maps?q=${lat},${lng}`;
      window.open(url, "_blank");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    return date.toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  // Extract data - GPS aus description.location.coords
  const locationData = currentData?.description?.location || {};
  const coords = locationData.coords || {};
  const latitude = coords.latitude;
  const longitude = coords.longitude;
  const title = currentData?.title || "";
  const created = currentData?.created;
  const modified = currentData?.modified;
  const externalIds = currentData?.external_ids || [];
  const displayId = objectId?.replace("IoT:", "") || "";
  const sensorType = currentData?.description?.sensorType;

  return (
    <div className="min-h-[100dvh] bg-[var(--sa-light)]">
      {/* Fixed Header with scroll shadow */}
      <header className={`fixed top-0 left-0 right-0 z-20 bg-white transition-shadow duration-200 ${isScrolled ? 'shadow-md' : 'shadow-sm'}`}>
        <div className="flex items-center justify-between px-4 py-3 safe-area-top">
          <div className="flex items-center gap-3">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="icon"
              onClick={onBack}
              className="shrink-0"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--sa-secondary)]" />
            </Button>
            <img 
              src="/safety-assist-logo.png" 
              alt="SafetyAssist" 
              className="h-8 object-contain"
            />
          </div>
          <Button
            data-testid="refresh-btn"
            variant="ghost"
            size="icon"
            onClick={refreshData}
            disabled={isRefreshing}
          >
            <RefreshCw className={`w-5 h-5 text-[var(--sa-secondary)] ${isRefreshing ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="pt-20 pb-52 px-4 safe-area-bottom animate-slide-up mb-52">
        <div className="max-w-lg mx-auto space-y-4">
          {/* Title Card */}
          <Card className="p-5 bg-white shadow-sm border-0 rounded-xl">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium uppercase tracking-wider text-[var(--sa-primary)] mb-1">
                  Bezeichnung
                </p>
                <h1 className="text-xl font-bold text-[var(--sa-secondary)] break-words">
                  {title || "Unbenannt"}
                </h1>
                {sensorType && (
                  <div className="inline-flex items-center gap-1.5 mt-2 px-3 py-1 bg-[var(--sa-primary)]/10 rounded-full" data-testid="sensor-type-badge">
                    {sensorType === "alarm" ? (
                      <AlertTriangle className="w-3.5 h-3.5 text-[var(--sa-primary)]" />
                    ) : (
                      <Radio className="w-3.5 h-3.5 text-[var(--sa-primary)]" />
                    )}
                    <span className="text-xs font-semibold text-[var(--sa-primary)]">
                      {sensorType === "alarm" ? "Alarmpunkt" : "Tracker"}
                    </span>
                  </div>
                )}
              </div>
              <div className="px-3 py-1 bg-green-50 rounded-full">
                <span className="text-xs font-semibold text-green-600">Aktiv</span>
              </div>
            </div>
          </Card>

          {/* ID Card */}
          <Card className="p-5 bg-white shadow-sm border-0 rounded-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                  SafetyAssist-ID
                </p>
                <p className="font-mono text-base text-[var(--sa-secondary)]" data-testid="object-id">
                  {displayId}
                </p>
              </div>
              <Button
                data-testid="copy-id-btn"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(displayId)}
                className="shrink-0 border-gray-200"
              >
                {copied ? (
                  <Check className="w-4 h-4 text-green-600" />
                ) : (
                  <Copy className="w-4 h-4 text-gray-500" />
                )}
              </Button>
            </div>
          </Card>

          {/* Location Card */}
          {latitude && longitude && (
            <Card className="p-5 bg-white shadow-sm border-0 rounded-xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-[var(--sa-primary)]/10 rounded-lg flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[var(--sa-primary)]" />
                </div>
                <p className="text-sm font-semibold text-[var(--sa-secondary)]">
                  Standort
                </p>
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 bg-[var(--sa-light)] rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Breitengrad</p>
                  <p className="font-mono text-sm text-[var(--sa-secondary)]" data-testid="latitude-value">
                    {latitude.toFixed(6)}
                  </p>
                </div>
                <div className="p-3 bg-[var(--sa-light)] rounded-lg">
                  <p className="text-xs text-gray-500 mb-1">Längengrad</p>
                  <p className="font-mono text-sm text-[var(--sa-secondary)]" data-testid="longitude-value">
                    {longitude.toFixed(6)}
                  </p>
                </div>
              </div>

              <Button
                data-testid="open-maps-btn"
                variant="outline"
                onClick={openInMaps}
                className="w-full h-11 font-medium border-[var(--sa-primary)] text-[var(--sa-primary)] hover:bg-[var(--sa-primary)]/10"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                In Google Maps öffnen
              </Button>
            </Card>
          )}

          {/* Details Card */}
          <Card className="p-5 bg-white shadow-sm border-0 rounded-xl">
            <p className="text-sm font-semibold text-[var(--sa-secondary)] mb-4">
              Details
            </p>
            
            <div className="space-y-4">
              {created && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Clock className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Erstellt</p>
                    <p className="text-sm text-[var(--sa-secondary)]">{formatDate(created)}</p>
                  </div>
                </div>
              )}

              {modified && (
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                    <RefreshCw className="w-4 h-4 text-gray-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Zuletzt geändert</p>
                    <p className="text-sm text-[var(--sa-secondary)]">{formatDate(modified)}</p>
                  </div>
                </div>
              )}

              {externalIds && externalIds.length > 0 && (
                <div>
                  <p className="text-xs text-gray-500 mb-2">External IDs</p>
                  <div className="space-y-1">
                    {externalIds.map((extId, index) => (
                      <p key={index} className="font-mono text-xs text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                        {typeof extId === 'object' ? extId.id : extId}
                      </p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Edit Button */}
          <Button
            data-testid="edit-btn"
            onClick={onEdit}
            className="w-full h-14 bg-[var(--sa-secondary)] hover:bg-[var(--sa-secondary)]/90 text-white rounded-xl font-semibold text-base btn-press"
          >
            <Settings className="w-5 h-5 mr-2" />
            Objekt bearbeiten
          </Button>

          {/* Raw Data (collapsed) */}
          {currentData && (
            <details className="group mb-52">
              <summary className="cursor-pointer text-xs text-gray-400 hover:text-gray-600 transition-colors">
                Rohdaten anzeigen
              </summary>
              <Card className="mt-3 p-4 bg-gray-50 border-0 rounded-xl">
                <pre className="text-xs font-mono text-gray-600 whitespace-pre-wrap break-all">
                  {JSON.stringify(currentData, null, 2)}
                </pre>
              </Card>
            </details>
          )}
        </div>
      </main>
    </div>
  );
}
